import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import useThemeService from "../../../../../services/theme-service/ThemeService";

import { Upload as UploadSvg } from "../../../../../components/svg/svgImageList";
import Button from "../../../../../components/button/Button";
import { Dialog } from "../../../../../components/dialog/Dialog";
import { translationService } from "../../../../../services/translation/TranslationService";
import DropFiles from "../../../../../components/upload-drop-files/DropFiles";
import { FileExtended } from "../../../../../services/upload/FileUploadTracker";
import UploadTable from "../../../../../components/upload-drop-files/UploadTable";
import { ReportTemplatesStorage } from "../../../../../services/organization/ReportTemplatesStorage";
import {
  ApplicationError,
  errorHandler,
} from "../../../../../services/error-handler/ErrorHandlerService";
import "./composition.scss";
import WarningBeforeAction from "../../../../../components/data-components/WarningBeforeAction";
import { snackbarNotificationsService } from "../../../../../services/notifications/SnackbarNotificationsService";
import useModal from "../../../../../components/dialog/useModal";

interface IUploadButtonProps {
  reportTemplatesStorage: ReportTemplatesStorage;
}

export default observer(function UploadReportTemplateButton(props: IUploadButtonProps) {
  const { reportTemplatesStorage } = props;
  const { themeClass } = useThemeService();
  const { toggleModal: toggleUploadTemplateModal, isVisible: isUploadTemplateModalVisible } =
    useModal();
  const { toggleModal: toggleReplaceTemplateModal, isVisible: isReplaceTemplateModalVisible } =
    useModal();

  const [uploadedFiles, setUploadedFiles] = useState<Array<FileExtended>>([]);

  const uploadIsDisabled = useMemo(() => {
    return !uploadedFiles.length || reportTemplatesStorage.remainingStorageSizeIsLoading;
  }, [reportTemplatesStorage.remainingStorageSizeIsLoading, uploadedFiles.length]);

  const handleUploadTemplateConfirmation = useCallback(async () => {
    try {
      toggleUploadTemplateModal();
      await reportTemplatesStorage.uploadReportTemplate(uploadedFiles[0]);
    } catch (error) {
      const applicationError = error as ApplicationError;
      errorHandler.handleErrorInSnackbar(applicationError);
    }
  }, [reportTemplatesStorage, toggleUploadTemplateModal, uploadedFiles]);

  const handleUploadTemplateClick = useCallback(async () => {
    if (reportTemplatesStorage.remainingStorageSize < uploadedFiles[0].size) {
      setUploadedFiles([]);
      toggleUploadTemplateModal();
      snackbarNotificationsService.showError("general.upload.no-storage");
      return;
    }
    if (
      reportTemplatesStorage.data &&
      reportTemplatesStorage.hasItems &&
      reportTemplatesStorage.data.items.some((x: any) => x.name === uploadedFiles[0].name)
    )
      toggleReplaceTemplateModal();
    else await handleUploadTemplateConfirmation();
  }, [
    handleUploadTemplateConfirmation,
    reportTemplatesStorage.data,
    reportTemplatesStorage.hasItems,
    reportTemplatesStorage.remainingStorageSize,
    toggleReplaceTemplateModal,
    toggleUploadTemplateModal,
    uploadedFiles,
  ]);

  const addFilesForUpload = useCallback((files: Array<FileExtended>) => {
    if (!files.length || files.length > 1) return;
    setUploadedFiles(files);
  }, []);

  const removeFile = useCallback(() => {
    setUploadedFiles([]);
  }, []);

  const handleDialogClose = useCallback(() => {
    toggleUploadTemplateModal();
    setUploadedFiles([]);
  }, [toggleUploadTemplateModal]);

  useEffect(() => {
    async function getRemainingStorageSize() {
      await reportTemplatesStorage.getRemainingStorage();
    }
    if (isUploadTemplateModalVisible) getRemainingStorageSize();
  }, [isUploadTemplateModalVisible, reportTemplatesStorage]);

  return (
    <>
      <Button
        id="button-upload"
        icon={{
          svg: UploadSvg,
          textKey: "general.upload",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-upload"]),
        }}
        onClick={toggleUploadTemplateModal}
      />
      <Dialog
        sidePosition
        isVisible={isUploadTemplateModalVisible}
        onHide={handleDialogClose}
        titleKey="ReportTemplates.upload-modal-title"
        buttonOk={{
          textKey: "general.upload",
          action: handleUploadTemplateClick,
          disabled: uploadIsDisabled,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: handleDialogClose,
        }}
      >
        <div className="upload-modal-content">
          <div className="upload-details">
            {translationService.translate("ReportTemplates.upload-modal-details-part-1")}
            <span>
              {translationService.translate("ReportTemplates.upload-modal-details-part-2")}
            </span>
            {translationService.translate("ReportTemplates.upload-modal-details-part-3")}
          </div>
          <DropFiles onDroppedFiles={addFilesForUpload} fileTypesAccepted=".docx" />
          {uploadedFiles.length > 0 && (
            <UploadTable files={uploadedFiles} onRemoveFile={removeFile} />
          )}
        </div>
        <WarningBeforeAction
          isVisible={isReplaceTemplateModalVisible}
          titleKey="ReportTemplates.upload-modal-replace-file-title"
          buttonOk={{
            id: "replace",
            textKey: "ReportTemplates.upload-modal-replace-file-button",
            action: handleUploadTemplateConfirmation,
          }}
          toggleModal={toggleReplaceTemplateModal}
        >
          <div className="replace-file-modal-subtitle">
            {translationService.translate("ReportTemplates.upload-modal-replace-file-subtitle")}
            <span>{uploadedFiles[0]?.name ?? ""}</span>
          </div>
        </WarningBeforeAction>
      </Dialog>
    </>
  );
});
